import { TableFooter } from '@fukuri-admins/features/Table/TableFooter';
import { TableSearch } from '@fukuri-admins/ui/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { useClearableFilters } from '@fukuri-admins/utils/common';
import { CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getServices } from '../../../api/services';
import { useAuth } from '../../../components/auth/AuthProvider';
import { ContentsHeader } from '../../../components/common/ContentsHeader';
import { ServiceTableContent } from '../../../components/service/ServiceTableContent';
import { ServiceTableFilter } from '../../../components/service/ServiceTableFilter';
import { ServiceListType, ServiceType } from '../../../types/resources/ServiceType';
import { ActionTableColumnsType } from '../../../types/table/ActionTableColumnsType';
import {
  ActionTableSortParamsType,
  defaultActionTableSortParams,
} from '../../../types/table/ActionTableSortParamsType';
import { handleSorting } from '../../../utils/sortings';
import styles from './styles.module.scss';

export const IndexService = () => {
  const { signOut } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filteredServices, setFilteredServices] = useState<ServiceListType>([]);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [filterParams, setFilterParams] = useSearchParams();
  const [sortParams, setSortParams] = useState<ActionTableSortParamsType>({
    ...defaultActionTableSortParams,
    column: 'no',
  });
  const { showToast } = useToast();

  const serviceTableColumns: ActionTableColumnsType[] = [
    { label: '会社名', key: 'company.name', sortable: true, filterable: true },
    { label: 'サービス名', key: 'title', sortable: true, filterable: true },
    {
      label: 'サービスの概要',
      key: 'overview',
      sortable: true,
      filterable: true,
    },
    {
      label: 'カテゴリー',
      key: 'category.parent.name',
      sortable: true,
      filterable: true,
    },
    {
      label: '子カテゴリー',
      key: 'category.name',
      sortable: true,
      filterable: true,
    },
  ];

  const onClear = () => {
    setIsLoading(true);
    const newParams = new URLSearchParams(filterParams);
    newParams.delete('title');
    newParams.delete('category[code]');
    setFilterParams(newParams);
    setIsClearFilter(true);
  };

  const onSearch = () => {
    setIsLoading(true);
    getServicesData();
  };

  const handlePageChange = (page: number) => {
    setIsLoading(true);
    getServicesData(page);
  };

  const getServicesData = async (page?: number) => {
    try {
      const title = filterParams.get('title');
      const categoryCode = filterParams.get('category[code]');
      const pageNumber = page ? page : filterParams.get('page');
      const { data, headers } = await getServices(signOut, {
        ...(title && { title }),
        ...(categoryCode && { 'category[code]': categoryCode }),
        ...(pageNumber && { page: pageNumber.toString() }),
      });
      setTotalPage(Number(headers['total-pages']));
      setTotalCount(Number(headers['total-count']));
      const processedServices = data.services.map((service: ServiceType) => {
        return {
          id: service.id,
          title: service.title,
          overview: service.overview,
          detail: service.detail,
          coupon_url: service.coupon_url,
          coupon_code: service.coupon_code,
          coupon_text: service.coupon_text,
          important_information: service.important_information,
          supported_areas: service.supported_areas,
          site_url: service.site_url,
          is_giftee: service.is_giftee,
          is_first_time_free: service.is_first_time_free,
          main_image: service.main_image,
          logo_image: service.logo_image,
          cassette_image: service.cassette_image,
          company: service.company,
          category: service.category,
        };
      });
      setFilteredServices(processedServices);
    } catch {
      showToast({ message: 'データの取得に失敗しました。', status: 'danger' });
    } finally {
      setIsLoading(false);
    }
  };

  const { setIsClearFilter, filterKey } = useClearableFilters({
    filterParams,
    fetchFunction: getServicesData,
  });

  useEffect(() => {
    getServicesData();
  }, []);

  return (
    <div className={styles.Root} data-testid='services-page'>
      <ContentsHeader title='サービス一覧' />
      <div className={styles.tableContainer}>
        <div className={styles.Root__Table}>
          <div className={styles.searchContainer}>
            <TableSearch onSearch={onSearch} onClear={onClear}>
              <ServiceTableFilter key={filterKey} />
            </TableSearch>
          </div>
          {isLoading ? (
            <div className={styles.loading}>
              <CircularProgress size='1rem' color='inherit' />
            </div>
          ) : (
            <>
              <ServiceTableContent
                rows={filteredServices}
                columns={serviceTableColumns}
                sortParams={sortParams}
                headerSort={column =>
                  handleSorting(
                    column,
                    sortParams,
                    filteredServices,
                    setSortParams,
                    setFilteredServices
                  )
                }
              />
              <TableFooter
                onPageChange={handlePageChange}
                totalCount={totalCount}
                totalPage={totalPage}
                itemCount={filteredServices.length}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
